import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of, switchMap, tap } from 'rxjs';

import { APP_CONFIG } from '../config';
import type { AppConfig } from '../config';
import {
  isSessionPayload,
  mapLoginError,
  normalizeLoginCredentials,
  UNEXPECTED_LOGIN_ERROR_MESSAGE
} from './auth.flow';
import type { LoginCredentials, LoginResult, SessionPayload } from './auth.models';
import { AuthStore } from './auth.store';

@Injectable({ providedIn: 'root' })
export class AuthService {
  constructor(
    private readonly httpClient: HttpClient,
    private readonly authStore: AuthStore,
    @Inject(APP_CONFIG) private readonly config: AppConfig
  ) {}

  bootstrapSession(): Observable<boolean> {
    this.authStore.setLoading();

    return this.refreshSession();
  }

  signInEmail(credentials: LoginCredentials): Observable<LoginResult> {
    this.authStore.setLoading();

    return this.httpClient
      .post<unknown>(`${this.config.authBaseUrl}/sign-in/email`, normalizeLoginCredentials(credentials), {
        withCredentials: true
      })
      .pipe(
        switchMap(() => this.fetchSession()),
        map((session) => {
          if (!session) {
            throw new Error('Missing Better Auth session payload after sign-in.');
          }

          this.authStore.setAuthenticated(session);

          return { success: true } as const;
        }),
        catchError((error) => {
          this.authStore.setUnauthenticated();

          return of({
            success: false as const,
            message: error instanceof Error && error.message.includes('Missing Better Auth session payload')
              ? UNEXPECTED_LOGIN_ERROR_MESSAGE
              : mapLoginError(error)
          });
        })
      );
  }

  signOut(): Observable<boolean> {
    return this.httpClient.post<unknown>(`${this.config.authBaseUrl}/sign-out`, {}, { withCredentials: true }).pipe(
      tap(() => {
        this.authStore.setUnauthenticated();
      }),
      map(() => true),
      catchError(() => of(false))
    );
  }

  changeActiveStore(activeStoreId: string): Observable<boolean> {
    return this.httpClient.post<{ data: { activeStoreId: string | null } }>(
      `${this.config.apiBaseUrl}/users/me/active-store`,
      { activeStoreId }
    ).pipe(
      switchMap(() => this.refreshSession())
    );
  }

  private refreshSession(): Observable<boolean> {
    return this.fetchSession().pipe(
      tap((session) => {
        if (session) {
          this.authStore.setAuthenticated(session);
          return;
        }

        this.authStore.setUnauthenticated();
      }),
      map((session) => session !== null),
      catchError(() => {
        this.authStore.setUnauthenticated();
        return of(false);
      })
    );
  }

  private fetchSession(): Observable<SessionPayload | null> {
    return this.httpClient.get<unknown>(`${this.config.authBaseUrl}/get-session`, { withCredentials: true }).pipe(
      map((session) => (isSessionPayload(session) ? session : null))
    );
  }
}
